import React, { useId, useRef } from 'react';
import clsx from 'clsx';
import { APP_COLORS } from '@/config/colors';

export interface RatingStarsProps {
  /** Current rating value from 0 to max. Fractional values are supported in read-only mode. */
  value: number | null;
  /** Maximum rating value. Defaults to 5. */
  max?: number;
  /** Accessible label for the rating group. */
  ariaLabel: string;
  /** Called when a user selects a value in interactive mode. */
  onChange?: (value: number) => void;
  /** Renders stars without controls. */
  readOnly?: boolean;
  /** Size preset for each star. */
  size?: 'sm' | 'md' | 'lg';
  /** Optional wrapper class. */
  className?: string;
  /** Filled star color. Defaults to the project yellow token. */
  activeColor?: string;
  /** Empty star color. Defaults to the project gray token. */
  inactiveColor?: string;
  /** Forwarded to the first star button, e.g. to set initial focus in interactive mode. */
  firstStarRef?: React.Ref<HTMLButtonElement>;
}

const starPath =
  'M39.8943 14.36C39.7686 13.9898 39.5372 13.6645 39.2287 13.4243C38.9202 13.184 38.5481 13.0394 38.1583 13.0082L26.7562 12.1023L21.8222 1.18178C21.665 0.83005 21.4095 0.531308 21.0863 0.321608C20.7631 0.111908 20.3861 0.00021173 20.0008 3.00717e-07C19.6155 -0.000211129 19.2384 0.111071 18.915 0.320416C18.5916 0.529762 18.3357 0.828223 18.1782 1.17978L13.2441 12.1023L1.84206 13.0082C1.45897 13.0386 1.09274 13.1786 0.787167 13.4116C0.481599 13.6446 0.24964 13.9607 0.11904 14.3221C-0.0115608 14.6835 -0.0352721 15.0748 0.0507427 15.4493C0.136757 15.8238 0.328856 16.1656 0.604056 16.4338L9.0301 24.6467L6.05009 37.549C5.9596 37.9395 5.9886 38.3482 6.13333 38.7221C6.27805 39.096 6.53182 39.4177 6.8617 39.6456C7.19157 39.8734 7.58233 39.9969 7.98327 39.9999C8.3842 40.003 8.77679 39.8855 9.11011 39.6627L20.0002 32.4036L30.8902 39.6627C31.2309 39.8888 31.6327 40.0053 32.0415 39.9963C32.4503 39.9873 32.8466 39.8534 33.177 39.6125C33.5075 39.3717 33.7562 39.0354 33.8898 38.649C34.0234 38.2625 34.0354 37.8444 33.9243 37.451L30.2662 24.6527L39.3383 16.4897C39.9323 15.9538 40.1503 15.1179 39.8943 14.36Z';

const sizeClasses = {
  sm: 'h-5 w-5',
  md: 'h-7 w-7',
  lg: 'h-10 w-10',
};

const gapClasses = {
  sm: 'gap-1',
  md: 'gap-2',
  lg: 'gap-4',
};

const getFillPercentage = (star: number, value: number): number =>
  Math.min(Math.max(value - star + 1, 0), 1) * 100;

const clampRating = (value: number, max: number): number =>
  Math.min(Math.max(value, 1), max);

interface StarIconProps {
  fillPercentage: number;
  gradientId: string;
  className: string;
  activeColor: string;
  inactiveColor: string;
}

const StarIcon: React.FC<StarIconProps> = ({
  fillPercentage,
  gradientId,
  className,
  activeColor,
  inactiveColor,
}) => (
  <svg
    className={className}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset={`${fillPercentage}%`} stopColor={activeColor} />
        <stop offset={`${fillPercentage}%`} stopColor={inactiveColor} />
      </linearGradient>
    </defs>
    <path d={starPath} fill={`url(#${gradientId})`} />
  </svg>
);

export const RatingStars: React.FC<RatingStarsProps> = ({
  value,
  max = 5,
  ariaLabel,
  onChange,
  readOnly = false,
  size = 'md',
  className,
  activeColor = APP_COLORS.yellowDark,
  inactiveColor = APP_COLORS.gray[300],
  firstStarRef,
}) => {
  const id = useId();
  const starRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const safeValue = value ?? 0;
  const stars = Array.from({ length: max }, (_, index) => index + 1);
  const isInteractive = !readOnly && Boolean(onChange);

  const selectRating = (rating: number) => {
    if (!onChange) {
      return;
    }

    const nextRating = clampRating(rating, max);
    onChange(nextRating);
    starRefs.current[nextRating - 1]?.focus();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    currentValue: number,
  ) => {
    if (!onChange) {
      return;
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      selectRating((safeValue || currentValue) + 1);
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      selectRating((safeValue || currentValue) - 1);
    }

    if (event.key === 'Home') {
      event.preventDefault();
      selectRating(1);
    }

    if (event.key === 'End') {
      event.preventDefault();
      selectRating(max);
    }
  };

  if (!isInteractive) {
    return (
      <div
        className={clsx('flex items-center', gapClasses[size], className)}
        role="img"
        aria-label={`${ariaLabel}: ${safeValue} z ${max}`}
      >
        {stars.map((star) => (
          <StarIcon
            key={star}
            fillPercentage={getFillPercentage(star, safeValue)}
            gradientId={`${id}-star-${star}`}
            className={sizeClasses[size]}
            activeColor={activeColor}
            inactiveColor={inactiveColor}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={clsx('flex items-center', gapClasses[size], className)}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {stars.map((star) => (
        <button
          key={star}
          type="button"
          role="radio"
          aria-label={`${star} z ${max}`}
          aria-checked={safeValue === star}
          tabIndex={safeValue === star || (!safeValue && star === 1) ? 0 : -1}
          onClick={() => onChange?.(star)}
          onKeyDown={(event) => handleKeyDown(event, star)}
          ref={(node) => {
            starRefs.current[star - 1] = node;
            if (star === 1 && firstStarRef) {
              if (typeof firstStarRef === 'function') {
                firstStarRef(node);
              } else {
                (
                  firstStarRef as React.MutableRefObject<HTMLButtonElement | null>
                ).current = node;
              }
            }
          }}
          className="rounded-full transition-transform focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 hover:scale-105"
        >
          <StarIcon
            fillPercentage={getFillPercentage(star, safeValue)}
            gradientId={`${id}-star-${star}`}
            className={sizeClasses[size]}
            activeColor={activeColor}
            inactiveColor={inactiveColor}
          />
        </button>
      ))}
    </div>
  );
};
